import Button from '@kiwicom/orbit-components/lib/Button';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import { ReactElement } from 'react';
import ArrowUp from '@kiwicom/orbit-components/lib/icons/ArrowUp';
import { NavigateBack, usePostMessageToMobileApp } from '@kiwicom/web-message-schemas';
import useIsNativeMessageSupported from '@/common/services/hooks/useIsNativeMessageSupported';

const Shading = ({ children, hasShadow }: { children: ReactElement; hasShadow: boolean }) => {
  if (!hasShadow) return children;

  return <div className="rounded-full shadow-fixed shadow-cloud-dark">{children}</div>;
};

const BackButton = ({
  fallbackUrl = null,
  hasShadow = false,
  onClick,
  openMyTrips = false,
}: {
  fallbackUrl: string | null;
  hasShadow?: boolean;
  openMyTrips?: boolean;
  onClick: () => void | Promise<void>;
}) => {
  const intl = useIntl();
  const router = useRouter();
  const { postMessage } = usePostMessageToMobileApp();
  const navigateToMyTripsMessage: NavigateBack = {
    message: 'navigateBack',
  };
  const isNativeMessageSupported = useIsNativeMessageSupported('navigateBack');

  if (!fallbackUrl && window.history.length === 1) return null;

  const navigateBack = () => {
    if (window.history.length > 1) {
      return router.back();
    }

    if (!fallbackUrl) throw new Error('Missing fallbackUrl in BackButton');

    return void router.push(fallbackUrl);
  };

  const backButtonClickHandler = async () => {
    await onClick();
    if (openMyTrips && isNativeMessageSupported) {
      await postMessage(navigateToMyTripsMessage);
    } else {
      navigateBack();
    }
  };

  return (
    <Shading hasShadow={hasShadow}>
      <Button
        type="white"
        onClick={backButtonClickHandler}
        circled
        size="small"
        iconLeft={<ArrowUp size="large" className="-rotate-90 rtl:rotate-90" ariaHidden />}
        title={intl.formatMessage({ id: 'common.back', defaultMessage: 'Back' })}
      />
    </Shading>
  );
};

export default BackButton;
